import { useEffect } from 'react';

import { getAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import cookie from 'js/lib/cookie';
import redirect from 'js/lib/coursera.redirect';

import * as JWTLoginAPIUtils from 'bundles/saml-sso/utils/JWTLoginAPIUtils';

type JwtInviteCheckProps = {
  router: InjectedRouter;
  isProgramAdmin?: boolean;
  isAuthenticatedUser: boolean;
  programMembership: { isInvited?: boolean; isEmailNotVerified: boolean };
};

const useJwtInviteCheck = ({ router, programMembership, isProgramAdmin, isAuthenticatedUser }: JwtInviteCheckProps) => {
  useEffect(() => {
    const getTokenAndRedirect = async (organization: string, invitationToken: string, webToken?: string) => {
      try {
        const response = webToken
          ? await JWTLoginAPIUtils.getToken(organization, webToken)
          : await JWTLoginAPIUtils.getTokenFromInvitation(organization, invitationToken);
        if (response.id) {
          const returnUrlBuilder = new URL(window.location.href);
          returnUrlBuilder.searchParams.delete('organization');
          returnUrlBuilder.searchParams.delete('webToken');
          const returnUrl = returnUrlBuilder.toString();
          cookie.set('returnTo', returnUrl, { expires: 1 });
          redirect.setLocation(`/sso/return/jwt_invite/${response.id}?returnTo=${encodeURIComponent(returnUrl)}`);
        } else {
          router.replace({
            pathname: router.location.pathname,
            params: router.params,
            query: {
              ...router.location.query,
              organization: null,
              webToken: null,
              errorCode: 'invalidWebToken',
            },
          });
        }
      } catch {
        router.replace({
          pathname: router.location.pathname,
          params: router.params,
          query: {
            ...router.location.query,
            organization: null,
            webToken: null,
            errorCode: 'invalidWebToken',
          },
        });
      }
    };

    const { organization, webToken, invitationToken } = router.location.query;
    const authMode = getAuthMode(router.location.query as Record<string, unknown>, getLocationHash(router.location));
    const shouldTryJwtRedirect =
      // query params needed for jwt validation
      organization &&
      (webToken || invitationToken) &&
      // bypass for login form (has its own version of this)
      !authMode &&
      // bypass for learners not invited to the program, program membership will always be defined
      (!isAuthenticatedUser || programMembership.isInvited || programMembership.isEmailNotVerified) &&
      // bypass for admins
      !isProgramAdmin;

    if (!shouldTryJwtRedirect) return;

    getTokenAndRedirect(organization, invitationToken, webToken);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useJwtInviteCheck;
